import BackendService from 'BackendService';
import { useCallback, useEffect, useState } from 'react';
import {
  AppPostFormValues,
  appPostFormToPayload,
  DetectedNewsPost,
  NewsItemAdmin,
  NewsItemFormValues,
  newsFormToPayload,
} from '../../../models/NewsItem';

type ToastType = 'success' | 'info' | 'warning' | 'error';

const backend = new BackendService();

export function useNewsAdmin() {
  const [items, setItems] = useState<NewsItemAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const [toast, setToast] = useState<{ open: boolean; message: string; type: ToastType }>({
    open: false,
    message: '',
    type: 'info',
  });
  const showToast = useCallback((message: string, type: ToastType) => {
    setToast({ open: true, message, type });
  }, []);
  const closeToast = useCallback(() => setToast((t) => ({ ...t, open: false })), []);

  // Which "add/edit" modal is open — the website-linked form or the
  // app-only post form — or neither. Editing an existing item picks the
  // mode from its own sourceType; a brand-new item is created from whichever
  // "Add" entry point the admin used.
  const [formMode, setFormMode] = useState<'website' | 'app' | null>(null);
  const [editing, setEditing] = useState<NewsItemAdmin | null>(null);

  const [importOpen, setImportOpen] = useState(false);
  const [sourceUrl, setSourceUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [detected, setDetected] = useState<DetectedNewsPost[] | null>(null);
  const [selectedRefs, setSelectedRefs] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await backend.listNewsItems(showArchived));
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to load news items', 'error');
    } finally {
      setLoading(false);
    }
  }, [showArchived, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const openAddAppPost = useCallback(() => {
    setEditing(null);
    setFormMode('app');
  }, []);

  const openEdit = useCallback((item: NewsItemAdmin) => {
    setEditing(item);
    setFormMode(item.sourceType === 'app' ? 'app' : 'website');
  }, []);

  const closeForm = useCallback(() => {
    setFormMode(null);
    setEditing(null);
  }, []);

  const submitForm = useCallback(
    async (values: NewsItemFormValues) => {
      const payload = newsFormToPayload(values);
      try {
        if (editing) {
          await backend.updateNewsItem(editing.id, payload);
          showToast('News item updated', 'success');
        } else {
          await backend.createNewsItem(payload);
          showToast('News item added', 'success');
        }
        closeForm();
        await load();
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Save failed', 'error');
      }
    },
    [editing, closeForm, load, showToast],
  );

  const saveAppPost = useCallback(
    async (values: AppPostFormValues) => {
      const payload = appPostFormToPayload(values);
      try {
        if (editing) {
          await backend.updateNewsItem(editing.id, payload);
          showToast('App post updated', 'success');
        } else {
          await backend.createNewsItem(payload);
          showToast('App post saved as a draft', 'success');
        }
        closeForm();
        await load();
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Save failed', 'error');
      }
    },
    [editing, closeForm, load, showToast],
  );

  const publishAppPost = useCallback(
    async (values: AppPostFormValues) => {
      const payload = appPostFormToPayload(values);
      try {
        const saved = editing
          ? await backend.updateNewsItem(editing.id, payload)
          : await backend.createNewsItem(payload);
        await backend.publishNewsItem(saved.id);
        showToast('Published — now live in the app', 'success');
        closeForm();
        await load();
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Publish failed', 'error');
      }
    },
    [editing, closeForm, load, showToast],
  );

  const refreshFromSource = useCallback(
    async (id: string): Promise<NewsItemAdmin | null> => {
      try {
        const updated = await backend.refreshNewsItemFromSource(id);
        showToast('Refetched the latest content from the website', 'info');
        await load();
        return updated;
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Refresh failed', 'error');
        return null;
      }
    },
    [load, showToast],
  );

  const archiveItem = useCallback(
    async (item: NewsItemAdmin) => {
      if (!window.confirm(`Archive "${item.title}"? It will be removed from the app.`)) return;
      try {
        await backend.archiveNewsItem(item.id);
        showToast('News item archived', 'success');
        await load();
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Archive failed', 'error');
      }
    },
    [load, showToast],
  );

  const unarchiveItem = useCallback(
    async (item: NewsItemAdmin) => {
      try {
        await backend.unarchiveNewsItem(item.id);
        showToast('Restored as a draft — publish it to go live', 'success');
        await load();
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Unarchive failed', 'error');
      }
    },
    [load, showToast],
  );

  const publishItem = useCallback(
    async (item: NewsItemAdmin) => {
      try {
        await backend.publishNewsItem(item.id);
        showToast('Published — now live in the app', 'success');
        await load();
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Publish failed', 'error');
      }
    },
    [load, showToast],
  );

  const openImport = useCallback(async () => {
    setImportOpen(true);
    setDetected(null);
    setSelectedRefs(new Set());
    try {
      const { sourceUrl: url } = await backend.getNewsSource();
      setSourceUrl(url);
    } catch {
      setSourceUrl('');
    }
  }, []);

  const closeImport = useCallback(() => {
    setImportOpen(false);
    setDetected(null);
    setSelectedRefs(new Set());
  }, []);

  const scan = useCallback(async () => {
    setScanning(true);
    setDetected(null);
    setSelectedRefs(new Set());
    try {
      const result = await backend.scanNewsSource();
      setSourceUrl(result.sourceUrl);
      setDetected(result.detected);
      if (result.detected.length === 0) {
        showToast('No new posts found — everything on the site is already imported', 'info');
      }
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Scan failed', 'error');
    } finally {
      setScanning(false);
    }
  }, [showToast]);

  const toggleRef = useCallback((ref: string) => {
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedRefs((prev) => {
      if (!detected) return prev;
      if (prev.size === detected.length) return new Set();
      return new Set(detected.map((p) => p.sourceRef));
    });
  }, [detected]);

  const importSelected = useCallback(async () => {
    if (selectedRefs.size === 0) return;
    setImporting(true);
    try {
      const { importedCount, reinstatedCount, items } = await backend.importNewsItems([
        ...selectedRefs,
      ]);
      closeImport();
      await load();

      // A single import → jump straight into its edit form to review/publish.
      if (items.length === 1) {
        openEdit(items[0]);
        showToast('Imported as a draft — review, then publish', 'info');
        return;
      }

      const parts: string[] = [];
      if (importedCount > 0) parts.push(`${importedCount} imported as draft`);
      if (reinstatedCount > 0) parts.push(`${reinstatedCount} re-imported as draft`);
      showToast(parts.length ? parts.join(', ') : 'Nothing to import', 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  }, [selectedRefs, closeImport, load, openEdit, showToast]);

  return {
    items,
    loading,
    showArchived,
    setShowArchived,
    toast,
    closeToast,
    showToast,
    formMode,
    editing,
    openAddAppPost,
    openEdit,
    closeForm,
    submitForm,
    saveAppPost,
    publishAppPost,
    refreshFromSource,
    archiveItem,
    unarchiveItem,
    publishItem,
    importOpen,
    sourceUrl,
    scanning,
    detected,
    selectedRefs,
    importing,
    openImport,
    closeImport,
    scan,
    toggleRef,
    toggleAll,
    importSelected,
  };
}
